import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { Location } from "gen/company/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { UUID } from "shared-ts/classes/uuid"

import { AppState } from "app/store"
import { companyApiSlice } from "features/api/company"

export type SignInMethod = "key" | "text-message" | "email"

export type AuthState = {
  companyId: PBUUID | null
  companySearchUrl: string | null
  locations: Location[] | null
  location: Location | null
  defaultLocationId: PBUUID | null

  lastUsedStaffExternalID: string | null
  lastUsedSignInMethod: SignInMethod | null
}

export const initialState: AuthState = {
  companyId: null,
  companySearchUrl: null,
  locations: null,
  location: null,
  defaultLocationId: null,

  lastUsedStaffExternalID: null,
  lastUsedSignInMethod: null,
}

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLocation(state, action: PayloadAction<Location | null>) {
      state.location = action.payload
    },
    setLoggedInStaffCode(state, action: PayloadAction<string>) {
      state.lastUsedStaffExternalID = action.payload
    },
    setLastUsedSignInMethod(state, action: PayloadAction<SignInMethod>) {
      state.lastUsedSignInMethod = action.payload
    },
    clearState() {},
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      companyApiSlice.endpoints.getCompanyForSubdomain.matchFulfilled,
      (state, { payload }) => {
        state.companyId = payload.id ?? null
        state.companySearchUrl = payload.searchUrl ?? null
        state.defaultLocationId = payload.defaultLocationId ?? null

        // The selected location is persisted to localStorage, so it can outlive
        // the company it belonged to — after a company merge it names a store
        // that requests from this company are no longer allowed to reference.
        // Everything else here is refreshed from the server on load; this isn't,
        // so drop it whenever the company no longer offers it. Deactivated
        // locations count as not offered: they're still in the response, but
        // SignInLocationCombobox filters them out, so keeping one selected leaves a
        // store the user can't re-pick.
        const selected = state.location
        if (
          selected &&
          !(payload.locations ?? []).some(
            (loc) => loc.isActive && UUID.eqFromPB(loc.id, selected.id)
          )
        ) {
          state.location = null
        }
      }
    )
    builder.addMatcher(
      companyApiSlice.endpoints.listLocationsForCompany.matchFulfilled,
      (state, { payload, meta }) => {
        if (meta.arg.originalArgs.filterActive) {
          return
        }
        state.locations = payload.locations
        // Before sign-in the selected location only carries the public fields
        // from GetCompanyForSubdomain; swap in the full record. Dropping stale
        // selections is left to the getCompanyForSubdomain matcher.
        const selected = state.location
        const full = selected
          ? payload.locations.find((loc) => UUID.eqFromPB(loc.id, selected.id))
          : undefined
        if (full) {
          state.location = full
        }
      }
    )
  },
})

export const {
  setLocation,
  setLoggedInStaffCode,
  setLastUsedSignInMethod,
  clearState,
} = authSlice.actions

export const selectCompanyId = (state: AppState) => state.auth.companyId
export const selectCompanySearchUrl = (state: AppState) =>
  state.auth.companySearchUrl
export const selectLocation = (state: AppState) => {
  if (!state.auth.location && state.auth.defaultLocationId) {
    for (const loc of state.auth.locations ?? []) {
      if (UUID.eqFromPB(loc.id, state.auth.defaultLocationId)) {
        return loc
      }
    }
  }
  return state.auth.location
}
export const selectLocationWithoutDefault = (state: AppState) => {
  return state.auth.location
}
export const selectDefaultLocationTimeZone = (state: AppState) => {
  if (!state.auth.defaultLocationId) {
    return null
  }
  for (const loc of state.auth.locations ?? []) {
    if (UUID.eqFromPB(loc.id, state.auth.defaultLocationId)) {
      return loc.timeZone || null
    }
  }
  return null
}
export const selectLastUsedStaffExternalID = (state: AppState) =>
  state.auth.lastUsedStaffExternalID
export const selectLastUsedSignInMethod = (state: AppState) =>
  state.auth.lastUsedSignInMethod
